<script setup>
/**
 * Chat Rooms Settings Index - AI Focus
 * Lista de salas de chat para gerenciamento
 */
import { useAlert } from 'dashboard/composables';
import { useAdmin } from 'dashboard/composables/useAdmin';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import { computed, ref, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';

const store = useStore();
const { t } = useI18n();
const { isAdmin } = useAdmin();

const chatRoomsList = computed(() => store.getters['chatRooms/getChatRooms']);
const uiFlags = computed(() => store.getters['chatRooms/getUIFlags']);

const loading = ref({});

onMounted(() => {
  store.dispatch('chatRooms/get');
});

const deleteChatRoom = async ({ id }) => {
  try {
    loading.value[id] = true;
    await store.dispatch('chatRooms/delete', id);
    useAlert(t('CHAT_ROOMS_SETTINGS.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    useAlert(t('CHAT_ROOMS_SETTINGS.DELETE.API.ERROR_MESSAGE'));
  } finally {
    loading.value[id] = false;
  }
};

const showDeletePopup = ref(false);
const selectedRoom = ref({});

const openDelete = room => {
  showDeletePopup.value = true;
  selectedRoom.value = room;
};

const closeDelete = () => {
  showDeletePopup.value = false;
  selectedRoom.value = {};
};

const confirmDeletion = () => {
  deleteChatRoom(selectedRoom.value);
  closeDelete();
};

const deleteConfirmText = computed(
  () =>
    `${t('CHAT_ROOMS_SETTINGS.DELETE.CONFIRM.YES')} ${selectedRoom.value.name}`
);

const deleteRejectText = computed(() =>
  t('CHAT_ROOMS_SETTINGS.DELETE.CONFIRM.NO')
);

const confirmDeleteTitle = computed(() =>
  t('CHAT_ROOMS_SETTINGS.DELETE.CONFIRM.TITLE', {
    roomName: selectedRoom.value.name,
  })
);

const confirmPlaceHolderText = computed(() =>
  t('CHAT_ROOMS_SETTINGS.DELETE.CONFIRM.PLACE_HOLDER', {
    roomName: selectedRoom.value.name,
  })
);
</script>

<template>
  <div class="flex-1 overflow-auto">
    <BaseSettingsHeader
      :title="$t('CHAT_ROOMS_SETTINGS.HEADER')"
      :description="$t('CHAT_ROOMS_SETTINGS.DESCRIPTION')"
      feature-name="chat_rooms"
    >
      <template #actions>
        <router-link v-if="isAdmin" :to="{ name: 'settings_chat_rooms_new' }">
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('CHAT_ROOMS_SETTINGS.NEW_ROOM')"
          />
        </router-link>
      </template>
    </BaseSettingsHeader>

    <div class="mt-6 flex-1 text-n-slate-11">
      <woot-loading-state
        v-if="uiFlags.isFetching"
        :message="$t('CHAT_ROOMS_SETTINGS.LOADING')"
      />
      <p
        v-else-if="!chatRoomsList.length"
        class="flex flex-col items-center justify-center h-full text-base p-8"
      >
        {{ $t('CHAT_ROOMS_SETTINGS.LIST.404') }}
      </p>

      <table v-else class="min-w-full divide-y divide-n-weak">
        <tbody class="divide-y divide-n-weak">
          <tr v-for="room in chatRoomsList" :key="room.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <div class="flex items-center gap-3">
                <Avatar
                  :src="room.avatar_url"
                  :name="room.name || 'Room'"
                  :size="40"
                  rounded-full
                />
                <div>
                  <span class="block font-medium">{{ room.name }}</span>
                  <p v-if="room.description" class="mb-0 text-n-slate-10">
                    {{ room.description }}
                  </p>
                </div>
              </div>
            </td>

            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <span class="text-n-slate-10">
                {{ room.members?.length || 0 }}
                {{ $t('CHAT_ROOMS_SETTINGS.MEMBERS_COUNT') }}
              </span>
            </td>

            <td class="py-4 flex justify-end gap-1">
              <router-link
                :to="{
                  name: 'settings_chat_rooms_edit',
                  params: { roomId: room.id },
                }"
              >
                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('CHAT_ROOMS_SETTINGS.LIST.EDIT_ROOM')"
                  icon="i-lucide-settings"
                  slate
                  xs
                  faded
                />
              </router-link>

              <router-link
                :to="{
                  name: 'settings_chat_rooms_edit_members',
                  params: { roomId: room.id },
                }"
              >
                <Button
                  v-if="isAdmin"
                  v-tooltip.top="$t('CHAT_ROOMS_SETTINGS.LIST.MANAGE_MEMBERS')"
                  icon="i-lucide-users"
                  slate
                  xs
                  faded
                />
              </router-link>

              <Button
                v-if="isAdmin"
                v-tooltip.top="$t('CHAT_ROOMS_SETTINGS.DELETE.BUTTON_TEXT')"
                icon="i-lucide-trash-2"
                xs
                ruby
                faded
                :is-loading="loading[room.id]"
                @click="openDelete(room)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <woot-confirm-delete-modal
      v-if="showDeletePopup"
      v-model:show="showDeletePopup"
      :title="confirmDeleteTitle"
      :message="$t('CHAT_ROOMS_SETTINGS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="deleteConfirmText"
      :reject-text="deleteRejectText"
      :confirm-value="selectedRoom.name"
      :confirm-place-holder-text="confirmPlaceHolderText"
      @on-confirm="confirmDeletion"
      @on-close="closeDelete"
    />
  </div>
</template>
